import { useState, FormEvent } from 'react';
import { useSearchParams } from '../navigation';
import { useInsforge } from '../provider/InsforgeProvider';
import { usePublicAuthConfig } from '../hooks/usePublicAuthConfig';
import { ForgotPasswordForm } from './forms/ForgotPasswordForm';
import { ResetPasswordForm } from './forms/ResetPasswordForm';
import { emailSchema, createPasswordSchema } from '../lib/validation';
import { resolveAuthUrl } from '../lib/path-utils';

type ForgotPasswordStep = 'email' | 'password';

export interface ForgotPasswordProps {
  /** Text customization */
  title?: string;
  subtitle?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  submitButtonText?: string;
  loadingButtonText?: string;
  backToSignInText?: string;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Pre-built forgot password component with full business logic.
 *
 * Supports two password reset methods (auto-detected from backend config):
 * - **Link method**: Two-step flow (email → check email message)
 *   User receives an email with a reset link to complete the process in a new tab
 * - **Code method**: Three-step flow (email → verify code → reset password)
 *   User receives a verification code, enters it, then sets a new password
 *
 * @component
 * @example
 * ```tsx
 * <ForgotPassword
 *   onError={(error) => console.error('Error:', error)}
 * />
 * ```
 */
export function ForgotPassword({ onError, ...uiProps }: ForgotPasswordProps) {
  const { sendResetPasswordEmail, exchangeResetPasswordToken, resetPassword } = useInsforge();
  const { authConfig } = usePublicAuthConfig();
  const searchParams = useSearchParams();

  const [step, setStep] = useState<ForgotPasswordStep>('email');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showVerificationStep, setShowVerificationStep] = useState(false);

  // Step 1: Send reset email
  async function handleEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate email using Zod schema
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      const firstError = emailValidation.error.issues[0];
      setError(firstError.message);
      setLoading(false);
      return;
    }

    try {
      const result = await sendResetPasswordEmail(emailValidation.data);

      if (result?.success) {
        // Show verification step (link or code)
        setShowVerificationStep(true);
      } else {
        const errorMessage = result?.message || 'Failed to send reset email';
        setError(errorMessage);
        if (onError) {
          onError(new Error(errorMessage));
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
      setError(errorMessage);
      if (onError) {
        onError(new Error(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  }

  // Step 2: Verify code and get reset token (for code method)
  async function handleVerifyCode(code: string) {
    setError('');
    try {
      const result = await exchangeResetPasswordToken(email, code);

      if ('error' in result) {
        throw new Error(result.error.message || 'Failed to verify code');
      }

      if (result.token) {
        setResetToken(result.token);
        setStep('password');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid verification code';
      setError(errorMessage);
      if (onError) {
        onError(new Error(errorMessage));
      }
    }
  }

  // Resend reset email
  async function handleResendEmail() {
    await sendResetPasswordEmail(email);
  }

  // Step 3: Reset password
  async function handleResetPasswordSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password using dynamic Zod schema
    if (!authConfig) {
      setError('Configuration not loaded');
      setLoading(false);
      return;
    }

    const passwordZodSchema = createPasswordSchema({
      minLength: authConfig.passwordMinLength,
      requireUppercase: authConfig.requireUppercase,
      requireLowercase: authConfig.requireLowercase,
      requireNumber: authConfig.requireNumber,
      requireSpecialChar: authConfig.requireSpecialChar,
    });

    const passwordValidation = passwordZodSchema.safeParse(newPassword);
    if (!passwordValidation.success) {
      const firstError = passwordValidation.error.issues[0];
      setError(firstError.message);
      setLoading(false);
      return;
    }

    try {
      const result = await resetPassword(resetToken, newPassword);

      if (result?.message) {
        setSuccess(true);
        // Navigate to sign-in with all search params preserved and proper base path handling
        const signInUrl = resolveAuthUrl('/sign-in', searchParams);
        // Redirect after a short delay to show success message
        setTimeout(() => {
          window.location.href = signInUrl;
        }, 2000);
      } else {
        const errorMessage = 'Failed to reset password';
        setError(errorMessage);
        if (onError) {
          onError(new Error(errorMessage));
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
      if (onError) {
        onError(new Error(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  }

  // If email auth config not loaded yet
  if (!authConfig) {
    return null;
  }

  // Step 1 & 2: Email input and verification (handled by ForgotPasswordForm)
  if (step === 'email') {
    return (
      <ForgotPasswordForm
        email={email}
        onEmailChange={setEmail}
        onSubmit={(e) => {
          void handleEmailSubmit(e);
        }}
        error={error}
        loading={loading}
        showVerificationStep={showVerificationStep}
        resetPasswordMethod={authConfig.resetPasswordMethod}
        onVerifyCode={handleVerifyCode}
        onResendEmail={handleResendEmail}
        {...uiProps}
      />
    );
  }

  // Step 3: Password reset (only for code method)
  // Use ResetPasswordForm with the obtained resetToken
  return (
    <ResetPasswordForm
      newPassword={newPassword}
      confirmPassword={confirmPassword}
      onNewPasswordChange={setNewPassword}
      onConfirmPasswordChange={setConfirmPassword}
      onSubmit={(e) => void handleResetPasswordSubmit(e)}
      error={error}
      loading={loading}
      success={success}
      authConfig={authConfig}
      {...uiProps}
    />
  );
}
